import React, {useState, useCallback, useMemo} from "react";
import { Column, GridRow, message } from "@wellth/web-ui";
import { LoadingScreen } from "@wellth/ui";
import { find, get } from "lodash";
import moment from "moment-timezone";
import * as grid from "constants/grid";
import { MEMBER_PROFILE_REFETCH } from "utils";
import * as styles from "constants/styles";
import { AdminPrivilege } from "constants/adminPrivilege";
import KeyMemberInfo from "components/KeyMemberInfo";
import Info from "components/PersonInformationCard";
import TabCard from "components/TabCard";
import { Rewards } from "containers/Rewards";
import Description from "components/Description";
import PinReset from "components/PinReset";
import { AdherenceCard } from "components/Adherence";
import { ConditionsCard } from "components/Conditions";
import { OtherActions } from "containers/OtherActions";
import { PatientChat } from "containers/PatientChat";
import { PatientNotes } from "containers/PatientNotes";
import {
  useAdherenceCurrentRewardPeriodLazyQuery,
  useDialysisAppointmentSchedulesQuery,
  useMemberQuery,
  useMemberCalendarLazyQuery,
  useUpdateMemberByIdMutation,
} from "hooks/graphql";
import { ReminderMethod } from "components/EditReminderForm/inputs";
import { isAdminOrSuperAdmin, isCallCenterAdmin } from "utils/roles";
import {
  MemberStatusValue,
  PauseStatus,
  TypeOfEnrollmentOptions,
} from "types/globalTypes";
import { IdentityTag } from "containers/IdentityTag";
import { ActivityContainer } from "containers/Activity";
import { DialysisAppointments } from "containers/MemberProfile/components/DialysisAppointments";
import { DialysisMethod } from "components/DialysisAppointments";
import { Reminders } from "./components/Reminders";
import TaskCalendarContainer, {
  AppointmentType,
  TaskCalendarEvent,
} from "./components/TaskCalendar";
import MixpanelContainer from "../Mixpanel";
import * as memberProfileStyle from "./styles";
import * as constants from "./constants";
import {DATE_FORMAT} from "utils/date";

interface Primary {
  isPrimary: boolean;
}

const sortByPrimary = (multipleInputs) =>
  multipleInputs.sort((a) => (a.isPrimary ? -1 : 1));

const findMemberName = <T extends Primary>(objects: T[]): T | undefined => {
  const primaryName = find(objects, ({ isPrimary }) => isPrimary);
  if (!primaryName) {
    return objects.length > 0 ? objects[0] : undefined;
  }
  return primaryName;
};

export interface TestPatient {
  isLocalNotificationEnabled: boolean;
  isReminderMessagingEnabled: boolean;
  isProductTourRecurring: boolean;
}

export interface StatusChange {
  status: string;
  programCode: string;
  shortId: string;
  adminRole: string;
}

export interface MemberProfileProps {
  id: string;
  adminRole: AdminPrivilege;
  adminId: string;
  initialCalendarDate?: Date | null;
  routeCheckIn: (
    event: TaskCalendarEvent,
    memberTimezone: string,
    viewedDate: Date,
  ) => void;
  routeAppointment: (event: AppointmentType, memberTimezone: string) => void;
  routeTestPatient: (testPatient: TestPatient) => void;
  routeStatusChange: (statusChange: StatusChange) => void;
  routeDeleteMember: () => void;
  routeInfoModal: () => void;
  routeRewardModal: (
    periodEnd: Date,
    periodReward: number,
    periodSum: number,
    isAdmin: boolean,
    rewardPeriodId: string,
    periodStart: Date,
    settleDate: Date,
    pauseStatus: PauseStatus,
    programCode: string,
  ) => void;
  routeCreateReminder: (
    memberTimezone: string,
    programTemplate: string,
    programCode: string,
  ) => void;
  routeEditReminder: (
    reminderId: string,
    memberTimezone: string,
    programTemplate: string,
    programCode: string,
  ) => void;
  routeReminderInfo: (
    reminderId: string,
    memberTimezone: string,
    programTemplate: string,
    programCode: string,
  ) => void;
  routePauseModal: (pauseStatus: string) => void;
  routeEditPauseModal: (
    pauseDetails: { id: string; reason: string; note: string },
  ) => void;
  routeReOpenProspect: (programCode: string, shortId: string) => void;
  routeCreateDialysisAppointment: () => void;
  routeDeleteDialysisAppointment: (dialysisId: string) => void;
  routeEditDialysisAppointment: (dialysisId: string) => void;
}

export const MemberProfile: React.FC<MemberProfileProps> = ({
  adminRole,
  adminId,
  id,
  initialCalendarDate,
  routeCheckIn,
  routeTestPatient,
  routeStatusChange,
  routeDeleteMember,
  routeInfoModal,
  routeRewardModal,
  routeCreateReminder,
  routeEditReminder,
  routeReminderInfo,
  routePauseModal,
  routeEditPauseModal,
  routeReOpenProspect,
  routeCreateDialysisAppointment,
  routeDeleteDialysisAppointment,
  routeEditDialysisAppointment,
}) => {
  // State to track loaded calendar date range
  const [calendarDateRange, setCalendarDateRange] = useState({
    startDate: constants.calendarStartDate,
    endDate: constants.calendarEndDate,
  });


  const [
    getAdherence,
    {
      data: {
        member: { adherence: { creditedCheckInCount = null } = {} } = {},
      } = {},
    },
  ] = useAdherenceCurrentRewardPeriodLazyQuery({
    onError: (error) =>
      message.error(
        `Error fetching credited check in count for current reward period: ${error.message
        }`,
      ),
  });

  const {
    loading: memberLoading,
    error,
    data: {
      member: {
        description = null,
        isTestMember = null,
        reminders = [],
        isReminderEditingOpen = null,
        isRestfulMember = null,
        isReminderMessagingEnabled = null,
        isLocalNotificationEnabled = null,
        isProductTourRecurring = null,
        pauseStatus = null,
        pauseDetails = null,
        renderCalendarView = [],
        createdAt = null,
        latestTimezone = null,
        enrolledBy = null,
        typeOfEnrollment = null,
        person: {
          names = [],
          addresses = [],
          gender = "",
          birthDate = "",
          emailAddresses = [],
          phoneNumbers = [],
          shortId = "",
          primaryLanguage = "",
          primaryTimezone = "",
        } = {},
        currentOrNextRewardPeriodBalance: {
          periodSum = null,
          periodReward = null,
          periodEnd = null,
          rewardPeriodId = null,
          periodStart = null,
          settleDate = null,
        } = {},
        program: { id: programId = "", programCode = "", programTemplate = "" } = {},
        last30 = {
          __typename: "AdherenceSummary",
          requiredReminderCount: null,
          totalReminderCount: null,
          requiredValidCheckInCount: null,
          totalValidCheckInCount: null,
          percentRequiredRemindersAdherent: null,
          percentTotalRemindersAdherent: null,
          creditedCheckInCount: null,
          percentTotalRemindersNonCreditedAdherent: null,
        },
        last7 = {
          __typename: "AdherenceSummary",
          requiredReminderCount: null,
          totalReminderCount: null,
          requiredValidCheckInCount: null,
          totalValidCheckInCount: null,
          percentRequiredRemindersAdherent: null,
          percentTotalRemindersAdherent: null,
          creditedCheckInCount: null,
          percentTotalRemindersNonCreditedAdherent: null,
        },
        total = {
          __typename: "AdherenceSummary",
          requiredReminderCount: null,
          totalReminderCount: null,
          requiredValidCheckInCount: null,
          totalValidCheckInCount: null,
          percentRequiredRemindersAdherent: null,
          percentTotalRemindersAdherent: null,
          creditedCheckInCount: null,
          percentTotalRemindersNonCreditedAdherent: null,
        },
        status = null,
        wellthId = null,
        overallScore,
        externalId = null,
        enrollmentDate = null,
      } = {},
    } = {},
  } = useMemberQuery({
    variables: {
      id,
      last7StartDate: constants.last7StartDate,
      last30StartDate: constants.last30StartDate,
      totalStartDate: constants.totalStartDate,
      today: constants.today,
      endDate: constants.endDate,
    },
    onError: () => message.error("Unable to fetch member"),
    onCompleted: ({
      member: {
        currentOrNextRewardPeriodBalance: { periodStart, periodEnd },
      },
    }) => {
      if (periodStart && periodEnd) {
        getAdherence({ variables: { id, periodEnd, periodStart } });
      }
    },
  });

  // Lazy query for fetching additional calendar data
  const [
    fetchCalendarData,
    { data: additionalCalendarData }
  ] = useMemberCalendarLazyQuery();

  // Merge additional calendar data into main renderCalendarView
  const allCalendarEvents = useMemo(() => {
    const baseEvents = renderCalendarView || [];
    const additionalEvents = additionalCalendarData?.member?.renderCalendarView || [];

    // Merge and deduplicate by reminderId and reminderDate
    const eventsMap = new Map();
    [...baseEvents, ...additionalEvents].forEach(event => {
      const key = `${event.reminderId}-${event.reminderDate}`;
      if (!eventsMap.has(key)) {
        eventsMap.set(key, event);
      }
    });

    return Array.from(eventsMap.values());
  }, [renderCalendarView, additionalCalendarData]);

  // Handle calendar navigation and lazy-fetch more data
  const handleCalendarNavigate = useCallback(async (
    direction: string,
    currentDate: Date
  ) => {
    const currentMonth = moment(currentDate);
    const rangeStart = moment(calendarDateRange.startDate);
    const rangeEnd = moment(calendarDateRange.endDate);

    // Fetch previous data if user navigates backward near the edge
    if (direction === 'PREV') {
      const monthsFromStart = currentMonth.diff(rangeStart, 'months');

      // Trigger fetch when within 1 month of the loaded start date
      if (monthsFromStart <= 1) {
        const newStartDate = moment(calendarDateRange.startDate)
          .subtract(constants.CALENDAR_FETCH_WINDOW_MONTHS, 'months')
          .format(DATE_FORMAT);

        try {
          await fetchCalendarData({
            variables: {
              id,
              totalStartDate: newStartDate,
              endDate: calendarDateRange.startDate,
            },
          });

          setCalendarDateRange(prev => ({
            ...prev,
            startDate: newStartDate,
          }));
        } catch (error) {
          // Silent catch - calendar data fetch failed
        }
      }
    }

    // Fetch future data if user navigates forward near the edge
    if (direction === 'NEXT') {
      const monthsToEnd = rangeEnd.diff(currentMonth, 'months');

      // Trigger fetch when within 1 month of the loaded end date
      if (monthsToEnd <= 1) {
        const newEndDate = moment(calendarDateRange.endDate)
          .add(constants.CALENDAR_FETCH_WINDOW_MONTHS, 'months')
          .format(DATE_FORMAT);

        try {
          await fetchCalendarData({
            variables: {
              id,
              totalStartDate: calendarDateRange.endDate,
              endDate: newEndDate,
            },
          });

          setCalendarDateRange(prev => ({
            ...prev,
            endDate: newEndDate,
          }));
        } catch (error) {
          // Silent catch - calendar data fetch failed
        }
      }
    }
  }, [calendarDateRange, id, fetchCalendarData]);


  const {
    data: { dialysisAppointmentSchedules = [] } = {},
  } = useDialysisAppointmentSchedulesQuery({
    variables: {
      memberId: id,
    },
    onError: (daError) =>
      message.error(`Error fetching dialysis appointments: ${daError.message}`),
  });

  const filteredDialysisAppointmentSchedules = dialysisAppointmentSchedules.filter(
    (i) => moment(i.endDate).isSameOrAfter(moment().startOf("day")),
  );

  const [updateMember] = useUpdateMemberByIdMutation();

  if (memberLoading) return <LoadingScreen showLoadingIndicator />;

  const { timezoneName: timezone = "" } = latestTimezone || {};
  const { firstName: primaryFirstName = "", lastName: primaryLastName = "" } =
    findMemberName(names) || {};
  const {
    id: pauseId,
    reason,
    startDate,
    endDate,
    note,
    totalPausedDays,
    previousReason,
    previousStartDate,
    previousEndDate,
  } = pauseDetails || {};
  const memberStatus = get(status, "value", "[UNKNOWN]");

  return (
    <>
      {error ? <div style={memberProfileStyle.DISABLE_SCREEN_STYLES} /> : null}
      <div style={styles.CONTAINER_STYLES}>
        <>
          <GridRow
            style={styles.ROW_STYLES}
            type={grid.GRID_TYPE}
            justify={grid.GRID_JUSTIFY}
            gutter={grid.GUTTER}
          >
            <Column
              xs={grid.COLUMN_FULL_WIDTH}
              sm={grid.COLUMN_FULL_WIDTH}
              md={grid.COLUMN_THRID_WIDTH}
              lg={grid.COLUMN_THRID_WIDTH}
              xl={grid.COLUMN_THRID_WIDTH}
            >
              <div style={styles.CONTENT_STYLES}>
                <KeyMemberInfo
                  fullName={`${primaryFirstName} ${primaryLastName}`}
                  shortId={shortId}
                  isTestMember={isTestMember}
                  title="Member"
                  programCode={programCode}
                  status={memberStatus}
                  adminRole={adminRole}
                  impactTier={overallScore?.impactTier}
                  handleTestMember={() =>
                    routeTestPatient({
                      isLocalNotificationEnabled,
                      isReminderMessagingEnabled,
                      isProductTourRecurring,
                    })
                  }
                  handleChangeStatus={
                    memberStatus !== MemberStatusValue.Inactive
                      ? () => {
                        routeStatusChange({
                          status: memberStatus,
                          shortId,
                          programCode,
                          adminRole,
                        });
                      }
                      : null
                  }
                  handleDeleteMember={routeDeleteMember}
                  handleReOpeningProspect={
                    memberStatus === MemberStatusValue.Inactive
                      ? () => {
                        routeReOpenProspect(programCode, shortId);
                      }
                      : null
                  }
                />
              </div>

              <div style={styles.CONTENT_STYLES}>
                <IdentityTag wellthId={wellthId} />
              </div>

              <div style={styles.CONTENT_STYLES}>
                <ConditionsCard
                  externalId={externalId}
                  programId={programId}
                />
              </div>

              <div style={styles.CONTENT_STYLES}>
                <AdherenceCard
                  allTasksRemindersAdherence={{
                    last30: Number(last30.percentTotalRemindersAdherent),
                    last7: Number(last7.percentTotalRemindersAdherent),
                    total: Number(total.percentTotalRemindersAdherent),
                  }}
                  manualCreditDetails={{
                    totalCount: Number(total.creditedCheckInCount),
                    currentRewardPeriodCount: creditedCheckInCount,
                    adherenceWithout: Number(
                      total.percentTotalRemindersNonCreditedAdherent,
                    ),
                  }}
                />
              </div>
              <div style={styles.CONTENT_STYLES}>
                <Info
                  personProfile={{
                    language: primaryLanguage,
                    phoneNumbers: sortByPrimary(
                      phoneNumbers.map(({ phoneNumber, isPrimary }) => ({
                        phoneNumber,
                        isPrimary,
                      })),
                    ),
                    emailAddresses: sortByPrimary(
                      emailAddresses.map(({ emailAddress, isPrimary }) => ({
                        emailAddress,
                        isPrimary,
                      })),
                    ),
                    addresses: sortByPrimary(
                      addresses.map(
                        ({ street, street2, city, state, zip, isPrimary }) => ({
                          street,
                          street2,
                          city,
                          state,
                          zip,
                          isPrimary,
                        }),
                      ),
                    ),
                    personSince: moment(createdAt).format("LL"),
                    gender,
                    birthdate: birthDate,
                  }}
                  overallScore={overallScore}
                  localTime={moment.tz(timezone).format("h:mm A (z)")}
                  type="Member"
                  onInfoModalClick={routeInfoModal}
                  isSelfServe={
                    typeOfEnrollment === TypeOfEnrollmentOptions.SelfServe
                  }
                  enroller={enrolledBy}
                />
              </div>
              <div style={styles.CONTENT_STYLES}>
                <Rewards
                  memberId={id}
                  onRewardModalClick={() =>
                    routeRewardModal(
                      moment(periodEnd).toDate(),
                      periodReward,
                      periodSum,
                      isAdminOrSuperAdmin(adminRole),
                      rewardPeriodId,
                      moment(periodStart).toDate(),
                      moment(settleDate).toDate(),
                      pauseStatus,
                      programCode,
                    )
                  }
                  nextPayoutDate={moment(settleDate).toDate()}
                  nextPayOutAmount={periodSum}
                  maximumCurrentPayout={periodReward}
                />
              </div>
              <div style={styles.CONTENT_STYLES}>
                <Reminders
                  reminders={reminders}
                  programTemplate={programTemplate}
                  isReminderEditingOpen={isReminderEditingOpen}
                  onReminderModalClick={({ reminderId, method }) => {
                    switch (method) {
                      case ReminderMethod.Edit:
                        routeEditReminder(
                          reminderId,
                          timezone,
                          programTemplate,
                          programCode,
                        );
                        break;
                      case ReminderMethod.Create:
                        routeCreateReminder(
                          timezone,
                          programTemplate,
                          programCode,
                        );
                        break;
                      case ReminderMethod.Info:
                        routeReminderInfo(
                          reminderId,
                          timezone,
                          programTemplate,
                          programCode,
                        );
                        break;
                      default:
                        throw new Error(`Invalid Method provided ${method}`);
                    }
                  }}
                  handleReminderLockChange={async () => {
                    await updateMember({
                      variables: {
                        id,
                        memberPatch: {
                          isReminderEditingOpen: !isReminderEditingOpen,
                          person: {},
                        },
                      },
                      refetchQueries: MEMBER_PROFILE_REFETCH(id),
                      awaitRefetchQueries: true,
                    });
                  }}
                />
              </div>
              <div style={styles.CONTENT_STYLES}>
                <DialysisAppointments
                  appointments={filteredDialysisAppointmentSchedules}
                  onDialysisApptModalClick={({ dialysisId, method }) => {
                    switch (method) {
                      case DialysisMethod.Create:
                        routeCreateDialysisAppointment();
                        break;
                      case DialysisMethod.Delete:
                        routeDeleteDialysisAppointment(dialysisId);
                        break;
                      case DialysisMethod.Edit:
                        routeEditDialysisAppointment(dialysisId);
                        break;
                      default:
                        throw new Error(`Invalid Method Provided ${method}`);
                    }
                  }}
                />
              </div>
              <div style={styles.CONTENT_STYLES}>
                <OtherActions
                  canEditPause={
                    !isCallCenterAdmin(adminRole) && pauseStatus !== "ACTIVE"
                  }
                  routeEditPauseModal={() =>
                    routeEditPauseModal({ id: pauseId, reason, note })
                  }
                  isRestfulMember={isRestfulMember}
                  pauseStatus={pauseStatus}
                  reason={reason}
                  startDate={startDate}
                  endDate={endDate}
                  totalPausedDays={totalPausedDays}
                  previousReason={previousReason}
                  previousStartDate={previousStartDate}
                  previousEndDate={previousEndDate}
                  id={id}
                  onButtonClick={() => routePauseModal(pauseStatus)}
                />
              </div>
            </Column>
            <Column
              xs={grid.COLUMN_FULL_WIDTH}
              sm={grid.COLUMN_FULL_WIDTH}
              md={grid.COLUMN_HALF_WIDTH}
              lg={grid.COLUMN_HALF_WIDTH}
              xl={grid.COLUMN_HALF_WIDTH}
            >
              <div style={memberProfileStyle.CALENDAR_STYLES}>
                <TabCard
                  tabs={[
                    { key: "calendar", tab: "Calendar" },
                    { key: "activity", tab: "Activity" },
                  ]}
                  activeIndex={0}
                >
                  {({ key }) => {
                    switch (key) {
                      case "calendar":
                        return (
                          <TaskCalendarContainer
                            enrollmentDate={enrollmentDate}
                            primaryTimezone={primaryTimezone}
                            renderCalendarView={allCalendarEvents}
                            routeCheckIn={routeCheckIn}
                            onNavigate={handleCalendarNavigate}
                            initialDate={initialCalendarDate ?? undefined}
                            memberId={id}
                          />
                        );
                      case "activity":
                        return (
                          <>
                            <MixpanelContainer wellthId={wellthId} />
                            <ActivityContainer
                              wellthId={wellthId}
                              timezone={primaryTimezone}
                            />
                          </>
                        );
                      default:
                        return null;
                    }
                  }}
                </TabCard>
              </div>
            </Column>
            <Column
              xs={grid.COLUMN_FULL_WIDTH}
              sm={grid.COLUMN_FULL_WIDTH}
              md={grid.COLUMN_THRID_WIDTH}
              lg={grid.COLUMN_THRID_WIDTH}
              xl={grid.COLUMN_THRID_WIDTH}
            >
              <div style={styles.CONTENT_STYLES}>
                <PatientChat patientId={id} adminId={adminId} />
              </div>
              <div style={styles.CONTENT_STYLES}>
                <PatientNotes patientId={id} adminId={adminId} />
              </div>
              <div style={styles.CONTENT_STYLES}>
                <Description
                  descriptionText={description}
                  onClick={async (personDescription: string): Promise<void> => {
                    await updateMember({
                      variables: {
                        id,
                        memberPatch: {
                          description: personDescription,
                        },
                      },
                      refetchQueries: MEMBER_PROFILE_REFETCH(id),
                      awaitRefetchQueries: true,
                    });
                  }}
                />
              </div>
              <div style={styles.CONTENT_STYLES}>
                <PinReset memberId={id} />
              </div>
            </Column>
          </GridRow>
        </>
      </div>
    </>
  );
};

export default MemberProfile;
